"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import DataFetchingSpinner from "./spinners/DataFetchingSpinner";
import { isEmpty } from "@/utils/null-safety.utils";
import { getGroupLogo } from "@/lib/api/group";
import { useQuery } from "@tanstack/react-query";
import { useAxios } from "@/lib/http/axios-client";
import { usePathname, useSearchParams } from "next/navigation";

const Logo: React.FC<{
  className?: string;
}> = (
  props: { className?: string } = {
    className: "",
  },
) => {
  const fill = "fill-yellow-900 dark:fill-yellow-50";
  const [logo, setLogo] = useState("");
  const [isClient, setIsClient] = useState(false);
  const axiosClient = useAxios();
  const params = useSearchParams();
  const invitationCode = params.get("invitation_code");
  const pathname = usePathname();

  type GroupLogoResponse = {
    white_label_logo?: string;
    white_label_name?: string;
  };

  const { data, isLoading, isSuccess, isEnabled } = useQuery<GroupLogoResponse>(
    {
      queryFn: async () => {
        const res = await getGroupLogo(axiosClient, {
          groupId: "",
          invitationCode: invitationCode || "",
        });
        return res.data;
      },
      queryKey: ["group", invitationCode],
      enabled: !!invitationCode,
    },
  );

  useEffect(() => {
    if (invitationCode) {
      if (isSuccess && data) {
        localStorage.setItem("logo", data.white_label_logo || "");
        localStorage.setItem("logo_alt", data.white_label_name || "");
        setLogo(data.white_label_logo || "");
        setIsClient(true);
      }
    } else if (
      (isEmpty(invitationCode) && pathname.includes("/login")) ||
      pathname.includes("/verify")
    ) {
      localStorage.removeItem("logo");
      localStorage.removeItem("logo_alt");
      setLogo("");
      setIsClient(true);
    } else if (localStorage.getItem("logo")) {
      setLogo(localStorage.getItem("logo") ?? "");
      setIsClient(true);
    } else {
      setIsClient(true);
    }
  }, [isSuccess, data]);

  if (!isClient) {
    return <DataFetchingSpinner />;
  }

  return !isEmpty(logo) ? (
    <Image
      src={logo}
      alt={localStorage.getItem("logo_alt") ?? "High Five Academy Logo"}
      width={200}
      height={40}
      unoptimized
      className={`${props.className} object-contain lg:object-left w-[200px] h-[40px]`}
    />
  ) : (
    <svg
      height="30px"
      viewBox="0 0 244 57"
      className={props.className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M157.235 45.7007C149.253 45.7007 144.207 39.7789 144.207 31.6429C144.207 23.1979 150.18 18.1 157.08 18.1C165.113 18.1 169.799 24.1248 169.387 31.3854L169.284 33.1877H148.223C148.738 38.44 151.776 42.0446 157.235 42.0446C160.788 42.0446 163.568 40.6028 165.422 37.5646L168.46 39.5214C166.452 43.229 162.384 45.7007 157.235 45.7007ZM148.326 29.5831H165.422C165.422 25.1031 161.921 21.653 157.08 21.653C151.982 21.653 148.996 25.1031 148.326 29.5831Z"
        className={fill}
      />
      <path
        d="M129.074 45.0311L119.393 19.2842V18.7693H123.616L131.958 41.6326H133.039L141.175 18.7693H145.501V19.2842L135.82 45.0311H129.074Z"
        className={fill}
      />
      <path
        d="M114.026 45.0312V18.7694H118.094V45.0312H114.026ZM114.026 13.7745V8.52209H118.043V13.7745H114.026Z"
        className={fill}
      />
      <path
        d="M100.3 45.0313V22.3225H94.9963V18.7694H100.352V12.8991C100.352 8.62513 103.184 6.41089 106.634 6.41089H110.702V9.96396H107.252C105.656 9.96396 104.368 10.7879 104.368 12.8991V18.7694H112.195V22.3225H104.368V45.0313H100.3Z"
        className={fill}
      />
      <path
        d="M60.9097 45.0313V6.41089H64.9777V23.1979H66.0076C66.8315 20.5202 69.6121 18.1 73.7831 18.1C80.0654 18.1 83.6699 22.7344 83.6699 27.9868V45.0313H79.6019V28.8107C79.6019 24.6397 76.3578 21.859 72.5988 21.859C68.1703 21.859 64.9777 25.1032 64.9777 29.5831V45.0313H60.9097Z"
        className={fill}
      />
      <path
        d="M44.8015 56.5144C37.5923 56.5144 32.031 53.9912 32.031 48.5328C32.031 45.6492 34.2967 43.0745 37.5923 43.0745H38.1073V42.0446C36.1505 41.4782 35.1206 39.9334 35.1206 38.0281C35.1206 36.1743 36.2535 34.8355 37.3349 34.166C35.4811 32.5697 34.3482 30.2525 34.3482 27.3689C34.3482 21.5501 38.8797 18.1 44.647 18.1C45.8828 18.1 47.1187 18.3059 48.2516 18.7179L57.9324 18.2545V22.065H51.1352V23.0949C53.1435 23.7128 54.6883 25.7726 54.6883 28.2958C54.6883 33.4966 50.1568 36.7407 44.647 36.7407C43.2052 36.7407 41.7633 36.4833 40.4245 36.0198C39.8066 36.2258 39.0342 36.8952 39.0342 38.0281C39.0342 39.5214 40.476 40.1908 41.9178 40.1908H49.9509C54.9458 40.1908 58.3444 43.126 58.3444 47.8634C58.3444 55.124 50.2083 56.5144 44.8015 56.5144ZM36.1505 47.9149C36.1505 51.8799 40.5275 52.8583 44.8015 52.8583C49.6419 52.8583 54.2249 51.7769 54.2249 47.8634C54.2249 45.5462 52.2681 43.8469 49.0755 43.8469H40.8879C37.9528 43.8469 36.1505 45.5977 36.1505 47.9149ZM38.3133 27.3689C38.3133 30.7675 40.785 33.2392 44.647 33.2392C48.4575 33.2392 50.9807 30.7675 50.9807 27.3689C50.9807 24.0733 48.4575 21.5501 44.647 21.5501C40.785 21.5501 38.3133 24.0733 38.3133 27.3689Z"
        className={fill}
      />
      <path
        d="M26.4058 45.0312V18.7694H30.4738V45.0312H26.4058ZM26.4058 13.7745V8.52209H30.4223V13.7745H26.4058Z"
        className={fill}
      />
      <path
        d="M0 45.0313V6.41089H4.06801V23.1979H5.09789C5.92179 20.5202 8.70246 18.1 12.8735 18.1C19.1557 18.1 22.7603 22.7344 22.7603 27.9868V45.0313H18.6923V28.8107C18.6923 24.6397 15.4482 21.859 11.6891 21.859C7.26063 21.859 4.06801 25.1032 4.06801 29.5831V45.0313H0Z"
        className={fill}
      />
      <path
        d="M237.479 13.3837L234.665 5.09131V4.80994H236.784L238.985 11.5133H239.333L241.6 4.80994H243.769V5.09131L240.806 13.3837L239.581 16.8595H237.33V16.5781L238.489 13.3837H237.479Z"
        className={fill}
      />
      <path
        d="M220.437 13.3836V4.8099H222.506V6.4485H222.837C223.069 5.47196 223.88 4.59473 225.171 4.59473C226.594 4.59473 227.422 5.42231 227.737 6.4485H228.068C228.349 5.45541 229.176 4.59473 230.434 4.59473C232.321 4.59473 233.248 6.00161 233.248 7.59056V13.3836H231.179V8.31883C231.179 7.29264 230.567 6.58092 229.541 6.58092C228.581 6.58092 227.902 7.30919 227.902 8.40159V13.3836H225.833V8.31883C225.833 7.29264 225.154 6.58092 224.161 6.58092C223.185 6.58092 222.506 7.35884 222.506 8.40159V13.3836H220.437Z"
        className={fill}
      />
      <path
        d="M214.489 13.5988C211.956 13.5988 210.069 11.7781 210.069 9.06366C210.069 6.48161 211.857 4.59473 214.439 4.59473C217.021 4.59473 218.593 6.58092 218.56 8.74918L218.544 9.79193H212.105C212.337 11.0829 213.247 11.7947 214.489 11.7947C215.399 11.7947 216.193 11.3147 216.558 10.7354L218.064 11.7616C217.352 12.854 216.16 13.5988 214.489 13.5988ZM212.155 8.12022H216.558C216.508 7.17677 215.647 6.29954 214.439 6.29954C213.264 6.29954 212.436 6.99471 212.155 8.12022Z"
        className={fill}
      />
      <path
        d="M203.167 13.5988C200.949 13.5988 199.41 11.6457 199.41 9.0968C199.41 6.54786 200.966 4.59477 203.167 4.59477C204.607 4.59477 205.583 5.4389 205.914 6.44855H206.245V0.969971H208.298V13.3837H206.245V11.7451H205.898C205.583 12.9037 204.607 13.5988 203.167 13.5988ZM201.512 9.0968C201.512 10.6858 202.571 11.712 203.928 11.712C205.285 11.712 206.328 10.603 206.328 9.0968C206.328 7.47475 205.219 6.48165 203.928 6.48165C202.571 6.48165 201.512 7.54095 201.512 9.0968Z"
        className={fill}
      />
      <path
        d="M192.947 13.5988C191.292 13.5988 189.984 12.5229 189.984 10.8347C189.984 9.24572 191.209 8.15332 193.162 8.15332H195.579V7.93815C195.579 7.02781 194.917 6.3492 193.874 6.3492C192.815 6.3492 192.318 7.02781 192.202 7.59056L190.365 7.07747C190.795 5.50506 192.103 4.59473 193.874 4.59473C196.34 4.59473 197.598 6.24989 197.598 7.93815V11.3312C197.598 11.6292 197.764 11.7285 198.028 11.7285H198.293V13.3836H197.085C196.241 13.3836 195.744 12.854 195.744 12.0926V11.7119H195.413C195.248 12.8871 194.321 13.5988 192.947 13.5988ZM192.07 10.785C192.07 11.4471 192.649 11.9105 193.46 11.9105C194.933 11.9105 195.579 10.9836 195.579 9.90779V9.69262H193.427C192.633 9.69262 192.07 10.0402 192.07 10.785Z"
        className={fill}
      />
      <path
        d="M184.388 13.5988C181.723 13.5988 179.836 11.6954 179.836 9.09676C179.836 6.51471 181.74 4.59473 184.388 4.59473C186.324 4.59473 187.797 5.55472 188.443 7.19333L186.655 8.12022C186.358 7.17677 185.497 6.48161 184.388 6.48161C182.981 6.48161 181.938 7.55746 181.938 9.09676C181.938 10.7023 182.998 11.7119 184.388 11.7119C185.431 11.7119 186.358 11.1326 186.705 10.0402L188.526 10.9009C187.897 12.6222 186.308 13.5988 184.388 13.5988Z"
        className={fill}
      />
      <path
        d="M173.373 13.5988C171.718 13.5988 170.411 12.5229 170.411 10.8347C170.411 9.24572 171.635 8.15332 173.589 8.15332H176.005V7.93815C176.005 7.02781 175.343 6.3492 174.3 6.3492C173.241 6.3492 172.744 7.02781 172.629 7.59056L170.791 7.07747C171.222 5.50506 172.529 4.59473 174.3 4.59473C176.766 4.59473 178.024 6.24989 178.024 7.93815V11.3312C178.024 11.6292 178.19 11.7285 178.455 11.7285H178.72V13.3836H177.511C176.667 13.3836 176.171 12.854 176.171 12.0926V11.7119H175.84C175.674 12.8871 174.747 13.5988 173.373 13.5988ZM172.496 10.785C172.496 11.4471 173.075 11.9105 173.886 11.9105C175.36 11.9105 176.005 10.9836 176.005 9.90779V9.69262H173.853C173.059 9.69262 172.496 10.0402 172.496 10.785Z"
        className={fill}
      />
    </svg>
  );
};

export default Logo;
